import React, { useState, useEffect } from "react";
import { API_BASE } from "../apiConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faChartLine,
  faChartBar,
  faBrain,
  faFilter,
  faCalendarAlt,
  faSearch,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faUsers,
  faHeartbeat,
  faUserMd,
  faRefresh,
  faDownload,
  faLightbulb,
  faClock,
  faUserNurse,
  faCalendarCheck,
  faBan,
  faHourglass,
  faStar,
  faEnvelope,
  faTimes,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import "./Analytics.css";
import PageHeader from "./layout/PageHeader";
import { BarChart3 } from "lucide-react";

const Analytics = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [data, setData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  // Filters
  const [dateRange, setDateRange] = useState("30"); // days
  const [scoreFilter, setScoreFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");


  useEffect(() => {
    fetchAnalytics();
  }, [activeLocation, dateRange, scoreFilter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const locId = activeLocation?.id || activeLocation?._id || "";
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/api/analytics?locationId=${locId}&days=${dateRange}&scoreFilter=${scoreFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load analytics");
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Analytics fetch failed:", err);
      setData(null);
      setErrorMsg(
        "We’re sorry. The server is busy right now—please try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAIAnalysis = async () => {
    setAiLoading(true);
    setErrorMsg("");
    try {
      const locId = activeLocation?.id || activeLocation?._id || "";
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/api/analytics/ai-analysis?locationId=${locId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error("AI analysis failed");
      }
      const result = await response.json();
      setAiAnalysis(result);
    } catch (err) {
      console.error("AI Analysis failed:", err);
      setAiAnalysis(null);
      setErrorMsg(
        "We’re sorry. The server is busy right now—please try again in a moment."
      );
    } finally {
      setAiLoading(false);
    }
  };

  // Export as PDF
  const handleExportPDF = async () => {
    setExportLoading(true);
    setShowExportMenu(false);
    try {
      const locId = activeLocation?.id || activeLocation?._id || "";
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/api/analytics/export-pdf?locationId=${locId}&days=${dateRange}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Analytics_Report_${
          new Date().toISOString().split("T")[0]
        }.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setExportMessage("PDF downloaded successfully!");
      } else {
        setExportMessage("Failed to generate PDF. Please try again.");
      }
    } catch (err) {
      console.error("PDF export failed:", err);
      setExportMessage("Failed to generate PDF. Please try again.");
    } finally {
      setExportLoading(false);
      setTimeout(() => setExportMessage(""), 3000);
    }
  };

  // Send report via email
  const handleSendEmail = async () => {
    if (!emailTo) return;
    setEmailSending(true);
    try {
      const locId = activeLocation?.id || activeLocation?._id || "";
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE}/api/analytics/send-report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          locationId: locId,
          days: dateRange,
          recipientEmail: emailTo,
          locationName: activeLocation?.name || "Your Agency",
          analysis: aiAnalysis, // Pass the current AI analysis to ensure consistency
        }),
      });

      if (response.ok) {
        setExportMessage("Report sent successfully!");
        setShowEmailModal(false);
        setEmailTo("");
      } else {
        setExportMessage("Failed to send email. Please try again.");
      }
    } catch (err) {
      console.error("Email send failed:", err);
      setExportMessage("Failed to send email. Please try again.");
    } finally {
      setEmailSending(false);
      setTimeout(() => setExportMessage(""), 3000);
    }
  };

  const getMockData = () => ({
    summary: {
      totalClients: 45,
      avgAipScore: 68,
      avgWellBeingScore: 72,
      avgCareTeamScore: 75,
      clientsImproved: 12,
      clientsDeclined: 3,
      newClients: 8,
    },
    appointments: {
      total: 156,
      completed: 128,
      delayed: 12,
      cancelled: 8,
      pending: 8,
      onTimeCount: 112,
      lateCount: 16,
      onTimeRate: 88,
      completionRate: 82,
      cancellationRate: 5,
      avgDuration: 45,
    },
    caregivers: {
      totalCaregivers: 12,
      totalNurses: 4,
      activeProviders: 14,
      avgOnTimeRate: 86,
      avgCompletionRate: 91,
      topPerformers: [
        {
          name: "Sarah Johnson",
          role: "Nurse",
          totalVisits: 32,
          onTimeRate: 97,
          completionRate: 100,
        },
        {
          name: "Mike Williams",
          role: "Caregiver",
          totalVisits: 28,
          onTimeRate: 93,
          completionRate: 96,
        },
        {
          name: "Emily Davis",
          role: "Caregiver",
          totalVisits: 25,
          onTimeRate: 88,
          completionRate: 92,
        },
      ],
    },
    scoreDistribution: {
      excellent: 15,
      moderate: 20,
      atRisk: 7,
      needData: 3,
    },
    trends: [
      { month: "Jul", avgScore: 62 },
      { month: "Aug", avgScore: 65 },
      { month: "Sep", avgScore: 64 },
      { month: "Oct", avgScore: 68 },
      { month: "Nov", avgScore: 70 },
      { month: "Dec", avgScore: 68 },
    ],
    categoryBreakdown: {
      wellBeing: { avg: 72, trend: "+3%" },
      careTeam: { avg: 75, trend: "+5%" },
      adl: { avg: 68, trend: "-1%" },
      iadl: { avg: 65, trend: "+2%" },
      environmental: { avg: 70, trend: "+4%" },
    },
    recentActivity: [
      {
        client: "Sarah J.",
        action: "Completed visit for John D.",
        date: "1 hour ago",
        type: "success",
      },
      {
        client: "Mike W.",
        action: "Checked in for Mary S.",
        date: "2 hours ago",
        type: "info",
      },
      {
        client: "Emily D.",
        action: "Visit delayed for Robert K.",
        date: "3 hours ago",
        type: "warning",
      },
    ],
  });

  const getMockAIAnalysis = () => ({
    businessHealth: {
      score: 78,
      status: "Good",
      color: "#52c41a",
      trend: "improving",
    },
    summary:
      "Your agency has a business health score of 78/100 (Good). With 156 visits and 88% on-time rate, operations are running smoothly.",
    strengths: [
      "Excellent staff punctuality (88% on-time rate)",
      "High visit completion rate (82%)",
      "Strong client well-being indicators",
    ],
    opportunities: [
      "12 delayed visits in the period need attention",
      "Staff punctuality can be improved from 88% to 95%",
      "5 clients at risk need care plan reviews",
    ],
    recommendations: [
      {
        priority: "High",
        action: "Implement punctuality tracking for late arrivals",
        impact: "Improve client satisfaction and reduce complaints",
      },
      {
        priority: "Medium",
        action: "Review care plans for at-risk clients",
        impact: "Improve client outcomes and retention",
      },
      {
        priority: "Low",
        action: "Complete assessments for pending clients",
        impact: "Better data for care planning and analytics",
      },
    ],
    staffInsights: {
      punctuality:
        "Staff punctuality at 88% is good but can be improved. Consider buffer time between visits.",
      reliability:
        "Visit completion rate of 82% is strong. Staff reliability is a key strength.",
    },
    prediction:
      "Based on current trends, your business metrics are projected to improve by 5-10% next quarter.",
  });

  const getHealthColor = (score) => {
    if (score >= 75) return "#52c41a";
    if (score >= 50) return "#faad14";
    return "#f5222d";
  };

  const headerContent = (
    <PageHeader
      title="Analytics"
      subtitle="Business intelligence and AI-powered insights."
      icon={<BarChart3 className="h-6 w-6 text-white" />}
      activeLocation={activeLocation}
      locations={locations}
      onChangeLocation={onChangeLocation}
      companyDisplayName={companyDisplayName}
      currentUser={currentUser}
      onLogout={onLogout}
      onLock={onLock}
    />
  );

  if (loading) {
    return (
      <div className="analytics-container page-shell">
        {headerContent}
        <div className="analytics-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }
  if (errorMsg) {
    return (
      <div className="analytics-container page-shell">
        {headerContent}
        <div className="analytics-error-banner">{errorMsg}</div>
      </div>
    );
  }

  return (
    <div className="analytics-container page-shell">
      {headerContent}
      <div className="analytics-actions">
        <button className="btn-refresh" onClick={fetchAnalytics}>
          <FontAwesomeIcon icon={faRefresh} /> Refresh
        </button>
        <div className="export-dropdown">
          <button
            className="btn-download"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Exporting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} /> Export
              </>
            )}
          </button>
          {showExportMenu && (
            <div className="export-menu">
              <div className="export-option" onClick={handleExportPDF}>
                <FontAwesomeIcon icon={faDownload} />
                <span>Download PDF</span>
              </div>
              <div
                className="export-option"
                onClick={() => {
                  setShowEmailModal(true);
                  setShowExportMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>Send via Email</span>
              </div>
            </div>
          )}
        </div>
        {exportMessage && (
          <div
            className={`export-message ${
              exportMessage.includes("Failed") ? "error" : "success"
            }`}
          >
            {exportMessage}
          </div>
        )}
      </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filter-group">
          <FontAwesomeIcon icon={faCalendarAlt} />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
        <div className="filter-group">
          <FontAwesomeIcon icon={faFilter} />
          <select
            value={scoreFilter}
            onChange={(e) => setScoreFilter(e.target.value)}
          >
            <option value="all">All Scores</option>
            <option value="excellent">Excellent (75+)</option>
            <option value="moderate">Moderate (40-74)</option>
            <option value="atRisk">At Risk (&lt;40)</option>
          </select>
        </div>
        <div className="search-group">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards Section - Hidden per user request */}
      {/* 
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon blue">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.summary?.totalClients || 0}</span>
            <span className="card-label">Total Clients</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon green">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.summary?.avgAipScore || 0}</span>
            <span className="card-label">Avg AIP Score</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon purple">
            <FontAwesomeIcon icon={faHeartbeat} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.summary?.avgWellBeingScore || 0}</span>
            <span className="card-label">Avg Well-Being</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon orange">
            <FontAwesomeIcon icon={faUserMd} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.summary?.avgCareTeamScore || 0}</span>
            <span className="card-label">Avg Care Team</span>
          </div>
        </div>
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon blue">
            <FontAwesomeIcon icon={faCalendarCheck} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.appointments?.total || 0}</span>
            <span className="card-label">Total Visits</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon green">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.appointments?.onTimeRate || 0}%</span>
            <span className="card-label">On-Time Rate</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon purple">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.appointments?.completionRate || 0}%</span>
            <span className="card-label">Completion Rate</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon" style={{background: 'linear-gradient(135deg, #f5222d, #ff4d4f)'}}>
            <FontAwesomeIcon icon={faHourglass} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.appointments?.delayed || 0}</span>
            <span className="card-label">Delayed Visits</span>
          </div>
        </div>
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon blue">
            <FontAwesomeIcon icon={faUserNurse} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.caregivers?.totalCaregivers || 0}</span>
            <span className="card-label">Caregivers</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon purple">
            <FontAwesomeIcon icon={faUserMd} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.caregivers?.totalNurses || 0}</span>
            <span className="card-label">Nurses</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon green">
            <FontAwesomeIcon icon={faStar} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.caregivers?.avgOnTimeRate || 0}%</span>
            <span className="card-label">Staff On-Time</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon orange">
            <FontAwesomeIcon icon={faBan} />
          </div>
          <div className="card-content">
            <span className="card-value">{data?.appointments?.cancelled || 0}</span>
            <span className="card-label">Cancelled</span>
          </div>
        </div>
      </div>
      */}

      {/* Main Analytics Grid */}
      <div className="analytics-grid">
        {/* AI Business Health Card */}
        <div className="analytics-card ai-card full-width">
          <div className="card-header">
            <h3>
              <FontAwesomeIcon icon={faBrain} /> AI Business Health Analysis
            </h3>
            <button
              className="btn-analyze"
              onClick={fetchAIAnalysis}
              disabled={aiLoading}
            >
              {aiLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Analyzing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faLightbulb} /> Analyze with AI
                </>
              )}
            </button>
          </div>

          {aiAnalysis ? (
            <div className="ai-analysis-content">
              {/* Health Score Circle */}
              <div className="health-score-section">
                <div
                  className="health-score-circle"
                  style={{ borderColor: aiAnalysis.businessHealth.color }}
                >
                  <span
                    className="score-value"
                    style={{ color: aiAnalysis.businessHealth.color }}
                  >
                    {aiAnalysis.businessHealth.score}
                  </span>
                  {/* <span className="score-label">Business Health</span> */}
                </div>
                <div className="health-status">
                  <span
                    className="status-badge"
                    style={{ backgroundColor: aiAnalysis.businessHealth.color }}
                  >
                    {aiAnalysis.businessHealth.status}
                  </span>
                  <span className="trend">
                    {aiAnalysis.businessHealth.trend === "improving"
                      ? "📈 Improving"
                      : aiAnalysis.businessHealth.trend === "declining"
                      ? "📉 Declining"
                      : "➡️ Stable"}
                  </span>
                </div>
              </div>

              {/* AI Summary */}
              <div className="ai-summary">
                <p>{aiAnalysis.summary}</p>
              </div>

              {/* Strengths & Opportunities */}
              <div className="insights-grid">
                <div className="insight-section strengths">
                  <h4>
                    <FontAwesomeIcon icon={faCheckCircle} /> Strengths
                  </h4>
                  <ul>
                    {aiAnalysis.strengths.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="insight-section opportunities">
                  <h4>
                    <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                    Opportunities
                  </h4>
                  <ul>
                    {aiAnalysis.opportunities.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Recommendations */}
              <div className="recommendations-section">
                <h4>📋 Recommendations</h4>
                <div className="recommendations-list">
                  {aiAnalysis.recommendations.map((rec, i) => (
                    <div
                      key={i}
                      className={`recommendation-item priority-${rec.priority.toLowerCase()}`}
                    >
                      <span className="priority-badge">{rec.priority}</span>
                      <div className="rec-content">
                        <strong>{rec.action}</strong>
                        <span className="impact">{rec.impact}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Staff Insights */}
              {aiAnalysis.staffInsights && (
                <div className="staff-insights-section">
                  <h4>👥 Staff Performance Insights</h4>
                  <div className="staff-insights-grid">
                    <div className="staff-insight-card">
                      <div className="insight-icon">
                        <FontAwesomeIcon icon={faClock} />
                      </div>
                      <div className="insight-content">
                        <strong>Punctuality</strong>
                        <p>{aiAnalysis.staffInsights.punctuality}</p>
                      </div>
                    </div>
                    <div className="staff-insight-card">
                      <div className="insight-icon">
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </div>
                      <div className="insight-content">
                        <strong>Reliability</strong>
                        <p>{aiAnalysis.staffInsights.reliability}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Prediction */}
              <div className="prediction-section">
                <FontAwesomeIcon icon={faChartLine} />
                <p>{aiAnalysis.prediction}</p>
              </div>
            </div>
          ) : (
            <div className="ai-placeholder">
              <FontAwesomeIcon icon={faBrain} size="3x" />
              <p>
                Click "Analyze with AI" to get intelligent insights about your
                business health
              </p>
            </div>
          )}
        </div>

        {/* Score Distribution Chart */}
        <div className="analytics-card">
          <div className="card-header">
            <h3>
              <FontAwesomeIcon icon={faChartPie} /> Score Distribution
            </h3>
          </div>
          <div className="chart-container pie-chart">
            <div className="pie-visual">
              {data?.scoreDistribution && (
                <div className="pie-segments">
                  <div
                    className="segment excellent"
                    style={{
                      "--percentage": `${
                        (data.scoreDistribution.excellent /
                          data.summary.totalClients) *
                        100
                      }%`,
                    }}
                  />
                </div>
              )}
            </div>
            <div className="pie-legend">
              <div className="legend-item">
                <span className="legend-color excellent"></span>
                <span className="legend-label">Excellent (75+)</span>
                <span className="legend-value">
                  {data?.scoreDistribution?.excellent || 0}
                </span>
              </div>
              <div className="legend-item">
                <span className="legend-color moderate"></span>
                <span className="legend-label">Moderate (40-74)</span>
                <span className="legend-value">
                  {data?.scoreDistribution?.moderate || 0}
                </span>
              </div>
              <div className="legend-item">
                <span className="legend-color at-risk"></span>
                <span className="legend-label">At Risk (&lt;40)</span>
                <span className="legend-value">
                  {data?.scoreDistribution?.atRisk || 0}
                </span>
              </div>
              <div className="legend-item">
                <span className="legend-color need-data"></span>
                <span className="legend-label">Need Data</span>
                <span className="legend-value">
                  {data?.scoreDistribution?.needData || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="analytics-card">
          <div className="card-header">
            <h3>
              <FontAwesomeIcon icon={faChartLine} /> Score Trend
            </h3>
          </div>
          <div className="chart-container trend-chart">
            <div className="trend-bars">
              {data?.trends?.map((item, i) => (
                <div key={i} className="trend-bar-group">
                  <div
                    className="trend-bar"
                    style={{ height: `${item.avgScore}%` }}
                  >
                    <span className="bar-value">{item.avgScore}</span>
                  </div>
                  <span className="bar-label">{item.month}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="analytics-card">
          <div className="card-header">
            <h3>
              <FontAwesomeIcon icon={faChartBar} /> Category Performance
            </h3>
          </div>
          <div className="category-breakdown">
            {data?.categoryBreakdown &&
              Object.entries(data.categoryBreakdown).map(([key, value]) => (
                <div key={key} className="category-item">
                  <div className="category-header">
                    <span className="category-name">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </span>
                    <span
                      className={`category-trend ${
                        value.trend.startsWith("+") ? "positive" : "negative"
                      }`}
                    >
                      {value.trend}
                    </span>
                  </div>
                  <div className="category-bar-bg">
                    <div
                      className="category-bar-fill"
                      style={{
                        width: `${value.avg}%`,
                        backgroundColor: getHealthColor(value.avg),
                      }}
                    />
                  </div>
                  <span className="category-value">{value.avg}/100</span>
                </div>
              ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="analytics-card">
          <div className="card-header">
            <h3>📊 Recent Activity</h3>
          </div>
          <div className="activity-list">
            {data?.recentActivity?.map((item, i) => (
              <div key={i} className={`activity-item ${item.type}`}>
                <div className="activity-icon">
                  {item.type === "success" && (
                    <FontAwesomeIcon icon={faCheckCircle} />
                  )}
                  {item.type === "warning" && (
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  )}
                  {item.type === "info" && (
                    <FontAwesomeIcon icon={faChartLine} />
                  )}
                </div>
                <div className="activity-content">
                  <strong>{item.client}</strong>
                  <span>{item.action}</span>
                </div>
                <span className="activity-time">{item.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div
          className="email-modal-overlay"
          onClick={() => setShowEmailModal(false)}
        >
          <div className="email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-modal-header">
              <h3>
                <FontAwesomeIcon icon={faEnvelope} /> Send Analytics Report
              </h3>
              <button
                className="close-btn"
                onClick={() => setShowEmailModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="email-modal-body">
              <p>
                Send the analytics report as a PDF to the following email
                address:
              </p>
              <div className="email-input-group">
                <FontAwesomeIcon icon={faEnvelope} />
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                />
              </div>
              <div className="email-preview">
                <strong>Report includes:</strong>
                <ul>
                  <li>Business Health Score & AI Analysis</li>
                  <li>Client Statistics</li>
                  <li>Appointment Metrics (On-time, Delayed, Completed)</li>
                  <li>Caregiver/Nurse Performance</li>
                  <li>Recommendations</li>
                </ul>
              </div>
            </div>
            <div className="email-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowEmailModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-send"
                onClick={handleSendEmail}
                disabled={!emailTo || emailSending}
              >
                {emailSending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} /> Send Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
